import React, { useRef, useState, useEffect } from "react";
import { supabase } from "./lib/supabaseClient";
import { simpleBookProcessor } from "./lib/simpleBookProcessor.js";
import AudioDemo from "./components/AudioDemo";

export default function HomePage() {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const fileInputRef = useRef();
  const textInputRef = useRef();

  // Add noindex meta tag for SEO
  useEffect(() => {
    const metaTag = document.createElement('meta');
    metaTag.name = 'robots';
    metaTag.content = 'noindex, nofollow';
    document.head.appendChild(metaTag);
    
    return () => {
      // Clean up meta tag when component unmounts
      const existingTag = document.head.querySelector('meta[name="robots"]');
      if (existingTag) {
        document.head.removeChild(existingTag);
      }
    };
  }, []);

  async function handleUpload(e) {
    e.preventDefault();
    console.log('📤 [UPLOAD] Starting file upload process...');
    console.log('🔄 [UPLOAD] Using enhanced debugging - timestamp:', new Date().toISOString());
    
    const file = fileInputRef.current.files[0];
    if (!file) {
      console.log('❌ [UPLOAD] No file selected');
      setUploadStatus("Please select a file.");
      return;
    }
    
    console.log('📤 [UPLOAD] File details:', {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified
    });
    
    if (!["application/pdf", "application/epub+zip"].includes(file.type) && !file.name.endsWith(".epub")) {
      console.log('❌ [UPLOAD] Unsupported file type:', file.type);
      setUploadStatus("Only PDF and EPUB files are allowed.");
      return;
    }
    
    setUploading(true);
    setUploadStatus("Uploading and processing book...");
    
    try {
      console.log('🚀 [UPLOAD] Starting simpleBookProcessor...');
      setUploadStatus("Processing book content...");
      
      // Use the simple processor with error handling
      const result = await simpleBookProcessor(file);
      
      console.log('🔧 [UPLOAD] Processor result:', {
        success: result.success,
        hasResult: !!result,
        resultKeys: Object.keys(result || {})
      });
      
      if (result && result.success) {
        setUploadStatus("Upload successful! Redirecting to dashboard...");
        console.log("✅ [UPLOAD] Upload successful, redirecting to dashboard...");
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1500);
      } else {
        console.warn('⚠️ [UPLOAD] Processor completed but success flag not set:', result);
        setUploadStatus("Processing completed but status unclear. Check dashboard.");
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 2000);
      }
      
    } catch (error) {
      console.error("❌ [UPLOAD] Upload failed with error:", error);
      console.error("❌ [UPLOAD] Error stack:", error.stack);
      console.error("❌ [UPLOAD] Error type:", typeof error);
      console.error("❌ [UPLOAD] Error constructor:", error.constructor.name);
      
      // Provide a user-friendly error message
      const errorMessage = error.message || "Upload failed. Please try again.";
      setUploadStatus("Upload failed: " + errorMessage);
    }
    
    setUploading(false);
    fileInputRef.current.value = "";
    if (textInputRef.current) textInputRef.current.value = "";
  }

  function handleTextInputClick() {
    if (!uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  function handleFileChange(e) {
    if (e.target.files && e.target.files[0] && textInputRef.current) {
      textInputRef.current.value = e.target.files[0].name;
    }
  }

  return (
    <div>
      {/* Header */}
      <header className="header">
        <div className="logo" style={{ cursor: 'pointer' }} onClick={() => window.location.href = '/home'}>
          <img alt="Light Reads logo" className="logo-img" src="/logo-quote.png" />
          <span className="logo-text">Light Reads</span>
        </div>
        <nav className="nav nav-light">
          <span style={{ cursor: 'pointer' }} onClick={() => window.location.href = '/about'}>For Authors and Publishers</span>
          <span style={{ cursor: 'pointer' }} onClick={() => window.location.href = '/about'}>About us</span>
        </nav>
      </header>

      {/* Hero Section */}
      <main className="hero">
        <h1 className="hero-title">
          Bring Your Story <span className="hero-title-accent">to Life.</span>
        </h1>
        <p className="hero-desc serif">
          Create immersive audiobooks and cinematic trailers from your manuscript in minutes.
        </p>
        <form className="hero-actions" onSubmit={handleUpload}>
          <div className="hero-input-wrapper">
            <input
              placeholder="Upload Your Book epub, PDF, mobi"
              className="hero-input"
              type="text"
              readOnly
              ref={textInputRef}
              onClick={handleTextInputClick}
              style={{ cursor: uploading ? 'not-allowed' : 'pointer', background: '#fff' }}
            />
            <input
              accept=".pdf,.epub,application/pdf,application/epub+zip"
              className="hero-input"
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              disabled={uploading}
              onChange={handleFileChange}
            />
          </div>
          <button
            type="submit"
            className="hero-btn"
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Create Audiobook"}
          </button>
        </form>
        {uploadStatus && <div className="text-sm mt-2 mb-4" style={{ color: uploadStatus.includes('success') ? 'green' : 'red' }}>{uploadStatus}</div>}
      </main>

      {/* Features Section */}
      <section className="features">
        <div>
          <h2 className="info-block-title">Upload your manuscript, and our AI studio does the rest.</h2>
          <p className="info-block-desc serif">
            Light Reads analyses your book to understand its setting, tone and characters. We suggest the best narrator style, generate a cast of AI voices for each character and even summarise your plot, so you start with a blueprint tailored to your story.
          </p>
        </div>
        <AudioDemo />
      </section>

      {/* Info Grid */}
      <section className="info-grid">
        <div className="info-block">
          <h3 className="info-block-title">Human-quality voices built for storytellers.</h3>
          <p className="info-block-desc serif">
            Our library of expressive voices covers a wide range of ages, accents and styles. Choose a single narrator for a consistent tone, or let each character speak in their own unique voice. Want to make changes? Edit pronunciation, pacing and emphasis with a click.
          </p>
        </div>
        <div className="info-block">
          <h3 className="info-block-title">Your creative control is paramount.</h3>
          <p className="info-block-desc serif">
            You decide how your book sounds. Easily swap voices, adjust the mood of a scene, or disable full-cast mode for straight narration. Our built-in pronunciation editor fixes tricky names and homonyms so the final product reflects your vision.
          </p>
        </div>
        <div className="info-block">
          <h3 className="info-block-title">Market your book like a pro.</h3>
          <p className="info-block-desc serif">
            Light Reads highlights key moments and turns them into a 30-second movie-trailer script, ready to feed into your favourite video generator. Promote your story across platforms with a cinematic teaser that matches the tone of your narration.
          </p>
        </div>
        <div className="info-block">
          <h3 className="info-block-title">Own your work. Distribute anywhere.</h3>
          <p className="info-block-desc serif">
            Export your audio or video files and publish on Audible, Spotify or your own website, no exclusivity and no hidden royalties. We never use your content to train voice models without permission.
          </p>
        </div>
      </section>

      {/* Built for authors and publishers */}
      <section className="built">
        <h3 className="built-title">Built for authors and publishers.</h3>
        <p className="built-desc serif">
          Skip the steep studio costs and confusing dashboards. Light Reads makes professional audio production accessible to every storyteller, whether you're publishing your first novel or scaling a backlist across multiple languages.
        </p>
      </section>

      {/* Footer */}
      <footer className="footer">
        Designed and built with love in Melbourne 📚 © 2025 Saturn Program Returns Pty Ltd
        <span className="footer-link">Privacy Policy</span>
        <span className="footer-link">Terms & Conditions</span>
      </footer>
    </div>
  );
}